import React, { ComponentType, lazy, Suspense } from 'react';
import { getEditorSubType } from '../../utils';
import { EditorSubType } from '@wix/editor-platform-sdk-types';

type DynamicImport<P> = () => Promise<{ default: ComponentType<P> }>;

export interface EditorComponentConfig<P> {
  default: DynamicImport<P>;
  overrides?: Partial<Record<EditorSubType, DynamicImport<P>>>;
}

export function resolveEditorComponent<P extends object>(
  config: EditorComponentConfig<P>,
): ComponentType<P> {
  const LazyDefault = lazy(config.default);

  const lazyOverrides = Object.fromEntries(
    Object.entries(config.overrides ?? {}).map(([key, importFn]) => [
      key,
      lazy(importFn),
    ]),
  ) as Partial<Record<EditorSubType, ComponentType<P>>>;

  const ResolvedComponent = (props: P) => {
    const editorSubType = getEditorSubType();
    const Component = (lazyOverrides[editorSubType] ??
      LazyDefault) as ComponentType<P>;

    return (
      <Suspense fallback={null}>
        <Component {...props} />
      </Suspense>
    );
  };

  return ResolvedComponent;
}
